import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShoppingCart, faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import { faInstagram, faTiktok } from "@fortawesome/free-brands-svg-icons";
import { useCart } from "../context/CartContext";
import { searchProducts } from "../utils/searchIndex";
import "../styles/Header.css";

const CATEGORY_NAV_ITEMS = [
  { label: "Shop", path: "/" },
  { label: "Stickers", path: "/shop?category=Stickers" },
  { label: "Bags", path: "/shop?category=Bags" },
  { label: "Keychains", path: "/shop?category=Keychains" },
  { label: "Towels", path: "/shop?category=Towels" },
  { label: "Enamel Pins", path: "/shop?category=Enamel%20Pins" },
  { label: "Photocard Holders", path: "/shop?category=Photocard%20Holders" },
  { label: "Sanitizer Keychains", path: "/shop?category=Sanitizer%20Keychains" },
  { label: "Collages", path: "/shop?category=Collages" },
  { label: "Hand Fans", path: "/shop?category=Hand%20Fans" },
  { label: "About", path: "/about" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { getCartCount, openCart } = useCart();
  const cartCount = getCartCount();
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mini search on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        // Keep search open if input is filled, otherwise close mini search dropdown
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false);
      setSearchQuery("");
      setMobileMenuOpen(false);
    }
  };

  const handleSelectSearchResult = (productId) => {
    navigate(`/product/${productId}`);
    setIsSearchOpen(false);
    setSearchQuery("");
    setMobileMenuOpen(false);
  };

  // MiniSearch fuzzy NLP search results
  const miniSearchResults = searchQuery.trim()
    ? searchProducts(searchQuery.trim(), 5)
    : [];

  const currentFullUrl = location.pathname + location.search;

  return (
    <header className={`header ${scrolled ? "header-scrolled" : ""}`}>
      {/* LEFT SECTION: Instagram & TikTok Icons */}
      <div className="nav-left">
        <a 
          href="https://www.instagram.com/star.ryhwa?igsh=NTc4MTIwNjQ2YQ==" 
          target="_blank" 
          rel="noopener noreferrer" 
          aria-label="Instagram"
          className="social-icon"
        >
          <FontAwesomeIcon icon={faInstagram} size="lg" />
        </a>
        <a 
          href="https://www.tiktok.com/@star.ryhwa?_r=1&_t=ZT-97YHF75VUir" 
          target="_blank" 
          rel="noopener noreferrer" 
          aria-label="TikTok"
          className="social-icon"
        >
          <FontAwesomeIcon icon={faTiktok} size="lg" />
        </a>
      </div>

      {/* MIDDLE SECTION: Main Header Navbar with Categories */}
      <nav className={`nav-middle ${isMobileMenuOpen ? "nav-open" : ""}`}>
        {CATEGORY_NAV_ITEMS.map((item) => {
          const isActive = 
            currentFullUrl === item.path ||
            (item.path === "/" && (location.pathname === "/" || location.pathname === "/shop") && !location.search);
          return (
            <Link
              key={item.label}
              to={item.path}
              className={`nav-link ${isActive ? "is-active" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* RIGHT SECTION: Search & Cart */}
      <div className="nav-right">
        <div className="search-container" ref={searchRef}>
          {isSearchOpen ? (
            <div className="search-wrapper">
              <form onSubmit={handleSearchSubmit} className="search-form">
                <input
                  type="text"
                  placeholder="Fuzzy search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                  autoFocus
                />
                <button type="button" className="search-close-btn" onClick={() => { setIsSearchOpen(false); setSearchQuery(""); }}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </form>

              {/* Live MiniSearch Fuzzy Results Dropdown */}
              {searchQuery.trim() !== "" && (
                <div className="mini-search-dropdown">
                  {miniSearchResults.length > 0 ? (
                    miniSearchResults.map((item) => (
                      <div
                        key={item.id}
                        className="mini-search-item"
                        onClick={() => handleSelectSearchResult(item.id)}
                      >
                        <img src={item.image} alt={item.name} className="mini-search-thumb" />
                        <div className="mini-search-info">
                          <span className="mini-search-title">{item.name}</span>
                          <span className="mini-search-price">{item.price}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="mini-search-no-results">
                      No results for "{searchQuery}"
                    </div>
                  )}
                  <button type="button" className="mini-search-view-all" onClick={handleSearchSubmit}>
                    View all results &rarr;
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="icon-btn search-btn" onClick={() => setIsSearchOpen(true)} aria-label="Search">
              <FontAwesomeIcon icon={faSearch} size="lg" />
            </button>
          )}
        </div>

        <button
          type="button"
          className="icon-btn cart-icon"
          aria-label={`Cart with ${cartCount} items`}
          onClick={() => {
            setMobileMenuOpen(false);
            openCart();
          }}
        >
          <FontAwesomeIcon icon={faShoppingCart} size="lg" />
          {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
        </button>

        {/* Hamburger Menu button for mobile */}
        <div className={`hamburger-menu ${isMobileMenuOpen ? "open" : ""}`} onClick={toggleMobileMenu}>
          <div className="bar"></div>
          <div className="bar"></div>
          <div className="bar"></div>
        </div>
      </div>
    </header>
  );
};

export default Header;
